/**
 * Cart upsell slider (cart-upsell-direct).
 *
 * Call initCartUpsellDirect() from cart-drawer-component - in
 * connectedCallback() (covers normal page loads) AND in open()
 * (covers the case where the drawer's inner HTML, including this
 * slider, only gets created the first time the drawer renders -
 * e.g. adding from an empty cart on the product page). Safe to
 * call as many times as you want from anywhere: listener setup
 * only ever runs once, per-track setup is idempotent.
 */

let installed = false;
let upsellAddPointer = null;
let upsellLinkPointer = null;
const snapTimers = new WeakMap();

function getShopRoot() {
  return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';
}

function getCurrency() {
  const el = document.querySelector('.cart-upsell-direct[data-currency]');
  if (el && el.dataset.currency) return el.dataset.currency;
  if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
    return window.Shopify.currency.active;
  }
  return 'EUR';
}

function formatMoney(cents) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: getCurrency()
    }).format(Number(cents) / 100);
  } catch (error) {
    return (Number(cents) / 100).toFixed(2);
  }
}

function findInPath(event, selector) {
  const path = event.composedPath ? event.composedPath() : [];
  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (node instanceof HTMLElement && node.matches(selector)) return node;
  }
  if (event.target && event.target.closest) return event.target.closest(selector);
  return null;
}

/* ---------- carousel ---------- */

function getTrackCards(track) {
  return Array.prototype.slice.call(track.querySelectorAll('.cart-upsell-direct__product'));
}

function getCardLeft(track, card) {
  const trackRect = track.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  return track.scrollLeft + cardRect.left - trackRect.left;
}

function getNearestCardIndex(track) {
  const cards = getTrackCards(track);
  if (!cards.length) return 0;
  let nearestIndex = 0;
  let nearestDistance = Infinity;
  cards.forEach((card, index) => {
    const distance = Math.abs(getCardLeft(track, card) - track.scrollLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });
  return nearestIndex;
}

function goToCard(track, index) {
  const cards = getTrackCards(track);
  if (!cards.length) return;
  const safeIndex = Math.max(0, Math.min(index, cards.length - 1));
  track.scrollTo({ left: getCardLeft(track, cards[safeIndex]), behavior: 'smooth' });
}

function snapToNearest(track) {
  const cards = getTrackCards(track);
  if (!cards.length) return;
  const targetLeft = getCardLeft(track, cards[getNearestCardIndex(track)]);
  if (Math.abs(track.scrollLeft - targetLeft) < 2) return;
  track.scrollTo({ left: targetLeft, behavior: 'smooth' });
}

function scheduleSnap(track) {
  const previousTimer = snapTimers.get(track);
  if (previousTimer) clearTimeout(previousTimer);
  snapTimers.set(track, setTimeout(() => snapToNearest(track), 120));
}

function initializeTrack(track) {
  if (!track || track.dataset.upsellTrackReady === 'true') return;
  track.dataset.upsellTrackReady = 'true';
  track.scrollLeft = 0;
  track.addEventListener('scroll', () => scheduleSnap(track), { passive: true });
}

function initializeAllTracks() {
  document.querySelectorAll('.cart-upsell-direct__track').forEach(initializeTrack);
}

/* ---------- theme cart update notification ---------- */

let themeEventsModule = null;

function notifyThemeCartUpdate(cart, sections) {
  const detail = {
    itemCount: cart.item_count,
    source: 'cart-upsell-direct',
    sections: sections || {}
  };

  const dispatchPlainFallback = () => {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: detail } }));
  };

  if (themeEventsModule) {
    try {
      document.dispatchEvent(new themeEventsModule.CartUpdateEvent(cart, 'cart-upsell-direct', detail));
      return;
    } catch (error) {
      dispatchPlainFallback();
      return;
    }
  }

  import('@theme/events')
    .then((mod) => {
      themeEventsModule = mod;
      try {
        document.dispatchEvent(new mod.CartUpdateEvent(cart, 'cart-upsell-direct', detail));
      } catch (error) {
        dispatchPlainFallback();
      }
    })
    .catch(dispatchPlainFallback);
}

function getAjaxCart() {
  return fetch(getShopRoot() + 'cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store'
  }).then((response) => {
    if (!response.ok) throw new Error('Unable to retrieve cart');
    return response.json();
  });
}

function syncSimpleCartUI(cart) {
  if (!cart) return;
  document.querySelectorAll('.checkout-button-total').forEach((el) => {
    el.textContent = formatMoney(cart.total_price);
  });
  document.querySelectorAll('[data-cart-count], .cart-count, .theme-drawer__badge').forEach((el) => {
    el.textContent = cart.item_count;
  });
}

function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    const id = component.dataset.sectionId;
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  if (ids.indexOf('cart-drawer-section') === -1) ids.unshift('cart-drawer-section');
  return ids.slice(0, 5);
}

/* ---------- add to cart ---------- */

function setLoading(button) {
  button.disabled = true;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
}

function setIdle(button, text) {
  button.disabled = false;
  button.classList.remove('is-loading', 'is-added');
  button.removeAttribute('aria-busy');
  if (text) button.textContent = text;
}

function setAdded(button) {
  button.classList.remove('is-loading');
  button.classList.add('is-added');
  button.removeAttribute('aria-busy');
  button.textContent = '\u2713';
}

function startUpsellAdd(button) {
  if (button.dataset.upsellInflight === 'true') return;
  if (button.classList.contains('is-added')) return;

  const product = button.closest('.cart-upsell-direct__product');
  const select = product && product.querySelector('[data-upsell-select]');
  const variantId = select ? Number(select.value) : 0;
  if (!variantId) return;

  button.dataset.upsellInflight = 'true';
  setLoading(button);

  fetch(getShopRoot() + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: getCartSectionIds().join(','),
      sections_url: window.location.pathname
    })
  })
    .then((response) =>
      response.json().then((result) => {
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || 'Unable to add product');
        }
        return result;
      })
    )
    .then((result) => {
      setAdded(button);
      return getAjaxCart().then((cart) => {
        notifyThemeCartUpdate(cart, result.sections);
        syncSimpleCartUI(cart);
        delete button.dataset.upsellInflight;
      });
    })
    .catch((error) => {
      console.error('Upsell add error:', error);
      if (document.body.contains(button)) {
        delete button.dataset.upsellInflight;
        button.classList.remove('is-loading', 'is-added');
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.textContent = 'Error';
        setTimeout(() => setIdle(button, 'Add'), 1200);
      }
    });
}

/* ---------- one-time listener installation ---------- */

function installListeners() {
  if (installed) return;
  installed = true;

  /* Arrows */
  document.addEventListener(
    'click',
    (event) => {
      const prevButton = findInPath(event, '[data-upsell-prev]');
      const nextButton = findInPath(event, '[data-upsell-next]');
      if (!prevButton && !nextButton) return;

      event.preventDefault();
      event.stopPropagation();

      const slider = (prevButton || nextButton).closest('.cart-upsell-direct__slider');
      const track = slider && slider.querySelector('.cart-upsell-direct__track');
      if (!track) return;

      initializeTrack(track);
      const currentIndex = getNearestCardIndex(track);
      goToCard(track, prevButton ? currentIndex - 1 : currentIndex + 1);
    },
    true
  );

  /* Variant change */
  document.addEventListener(
    'change',
    (event) => {
      const select = findInPath(event, '[data-upsell-select]');
      if (!select) return;
      const product = select.closest('.cart-upsell-direct__product');
      if (!product) return;
      const selected = select.options[select.selectedIndex];
      if (!selected) return;

      const price = Number(selected.dataset.price || 0);
      const compare = Number(selected.dataset.compare || 0);
      const priceElement = product.querySelector('[data-upsell-price]');
      const compareElement = product.querySelector('[data-upsell-compare]');

      if (priceElement) priceElement.textContent = formatMoney(price);
      if (compareElement) {
        if (compare > price) {
          compareElement.textContent = formatMoney(compare);
          compareElement.style.display = '';
        } else {
          compareElement.textContent = '';
          compareElement.style.display = 'none';
        }
      }
    },
    true
  );

  /* Product image -> product page */
  document.addEventListener(
    'pointerdown',
    (event) => {
      const link = findInPath(event, '[data-upsell-link]');
      upsellLinkPointer = link ? { link, x: event.clientX, y: event.clientY } : null;
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!upsellLinkPointer) return;
      const start = upsellLinkPointer;
      upsellLinkPointer = null;

      const link = findInPath(event, '[data-upsell-link]');
      if (!link || link !== start.link) return;

      const deltaX = Math.abs(event.clientX - start.x);
      const deltaY = Math.abs(event.clientY - start.y);
      if (deltaX > 10 || deltaY > 10) return;

      event.preventDefault();
      event.stopPropagation();
      const href = link.getAttribute('href');
      if (href) window.location.assign(href);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const link = findInPath(event, '[data-upsell-link]');
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();
      const href = link.getAttribute('href');
      if (href && !window.__upsellNavigating) {
        window.__upsellNavigating = true;
        window.location.assign(href);
      }
    },
    true
  );

  /* Add to cart - pointer flow, no disabling on pointerdown */
  document.addEventListener(
    'pointerdown',
    (event) => {
      const button = findInPath(event, '[data-upsell-add]');
      if (!button || button.dataset.upsellInflight === 'true' || button.classList.contains('is-added')) {
        upsellAddPointer = null;
        return;
      }
      upsellAddPointer = button;
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!upsellAddPointer) return;
      const pressed = upsellAddPointer;
      upsellAddPointer = null;

      const button = findInPath(event, '[data-upsell-add]');
      if (button === pressed) {
        event.preventDefault();
        event.stopPropagation();
        startUpsellAdd(pressed);
      }
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    () => {
      upsellAddPointer = null;
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = findInPath(event, '[data-upsell-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      startUpsellAdd(button);
    },
    true
  );

  /* Keep re-scanning for newly inserted tracks (drawer morphs etc.) */
  const observer = new MutationObserver((mutations) => {
    let foundTrack = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (
          (node.matches && node.matches('.cart-upsell-direct__track')) ||
          (node.querySelector && node.querySelector('.cart-upsell-direct__track'))
        ) {
          foundTrack = true;
        }
      });
    });
    if (foundTrack) requestAnimationFrame(initializeAllTracks);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * Call this from anywhere - cart-drawer-component's connectedCallback,
 * open(), #handleCartAdd, wherever. Listener installation happens once;
 * track scanning is cheap and idempotent, safe to repeat every call.
 */
window.initCartUpsellDirect = function () {
  installListeners();
  initializeAllTracks();
};


/* ==========================================
   PROTECTION PLAN TOGGLE
   ========================================== */

function findLineItemKeyForVariant(cart, variantId) {
  for (var i = 0; i < cart.items.length; i++) {
    if (cart.items[i].variant_id === variantId) return cart.items[i].key;
  }
  return null;
}

function handleProtectionChange(checkbox) {
  var wrapper = checkbox.closest('[data-protection-plan]');
  if (!wrapper) return;

  var variantId = Number(wrapper.dataset.variantId);
  if (!variantId) return;

  checkbox.disabled = true;
  var sectionIds = getCartSectionIds();

  function finish() {
    getAjaxCart().then(function (cart) {
      checkbox.disabled = false;
      // failsafe: re-sync checked state with actual cart contents
      checkbox.checked = !!findLineItemKeyForVariant(cart, variantId);
    });
  }

  if (checkbox.checked) {
    fetch(shopRoot + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (response) { return response.json(); })
      .then(function (result) {
        return getAjaxCart().then(function (cart) {
          notifyThemeCartUpdate(cart, result.sections);
          syncSimpleCartUI(cart);
          checkbox.disabled = false;
        });
      })
      .catch(function (error) {
        console.error('Protection plan add error:', error);
        checkbox.checked = false;
        checkbox.disabled = false;
      });
  } else {
    getAjaxCart().then(function (cart) {
      var key = findLineItemKeyForVariant(cart, variantId);
      if (!key) {
        checkbox.disabled = false;
        return null;
      }

      return fetch(shopRoot + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) { return response.json(); })
        .then(function (result) {
          return getAjaxCart().then(function (updatedCart) {
            notifyThemeCartUpdate(updatedCart, result.sections);
            syncSimpleCartUI(updatedCart);
            checkbox.disabled = false;
          });
        });
    }).catch(function (error) {
      console.error('Protection plan remove error:', error);
      checkbox.checked = true;
      checkbox.disabled = false;
    });
  }
}

document.addEventListener('change', function (event) {
  var checkbox = findInPath(event, '[data-protection-checkbox]');
  if (!checkbox) return;
  handleProtectionChange(checkbox);
}, true);